import React, { useMemo } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { CCTVCamera, CCTVStats } from '../utils/cctvTypes';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface CCTVChartsProps {
  cameras: CCTVCamera[];
  stats: CCTVStats;
}

const CCTVCharts: React.FC<CCTVChartsProps> = ({ cameras, stats }) => {
  // Pie chart data for status distribution
  const statusPieData = useMemo(() => ({
    labels: ['Working', 'Non-Working'],
    datasets: [{
      data: [stats.activeCameras, stats.inactiveCameras],
      backgroundColor: ['#27ae60', '#e74c3c'],
      borderColor: ['#fff', '#fff'],
      borderWidth: 2,
    }],
  }), [stats]);

  // Bar chart data for cameras by station
  const stationBarData = useMemo(() => {
    // Group cameras by station
    const stationCounts: Record<string, { working: number; nonWorking: number }> = {};

    cameras.forEach((camera) => {
      const station = camera.station || 'Unknown';
      if (!stationCounts[station]) {
        stationCounts[station] = { working: 0, nonWorking: 0 };
      }
      if (camera.status === 'active') {
        stationCounts[station].working++;
      } else {
        stationCounts[station].nonWorking++;
      }
    });

    // Sort by total cameras and take top 10
    const sortedStations = Object.entries(stationCounts)
      .sort((a, b) => (b[1].working + b[1].nonWorking) - (a[1].working + a[1].nonWorking))
      .slice(0, 10);

    return {
      labels: sortedStations.map(([station]) => station),
      datasets: [
        {
          label: 'Working',
          data: sortedStations.map(([, counts]) => counts.working),
          backgroundColor: '#27ae60',
          borderRadius: 4,
        },
        {
          label: 'Non-Working',
          data: sortedStations.map(([, counts]) => counts.nonWorking),
          backgroundColor: '#e74c3c',
          borderRadius: 4,
        },
      ],
    };
  }, [cameras]);

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 16,
          usePointStyle: true,
          font: { size: 12, weight: '500' as const },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            const total = stats.totalCameras;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          padding: 12,
          usePointStyle: true,
          font: { size: 11 },
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { size: 10 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: {
          stepSize: 1,
          font: { size: 10 },
        },
      },
    },
  };

  return (
    <div className="cctv-charts-section">
      <div className="charts-grid">
        {/* Status Distribution Pie Chart */}
        <div className="chart-card">
          <h3 className="chart-title">Camera Status Distribution</h3>
          <div className="chart-container pie-chart">
            <Pie data={statusPieData} options={pieOptions} />
          </div>
        </div>

        {/* Cameras by Station Bar Chart */}
        <div className="chart-card wide">
          <h3 className="chart-title">Cameras by Station (Top 10)</h3>
          <div className="chart-container bar-chart">
            <Bar data={stationBarData} options={barOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CCTVCharts;
